import { useCallback, useEffect, useState } from "react";
import { supabase, friendlyError } from "./supabase.js";
import { commsPath } from "./storageKey.js";

/* The month's documents and what they said.
 *
 * Documents are working material. Entries are the record. That split runs
 * through everything here: uploading a file is optional, deleting one is
 * ordinary housekeeping, and neither touches the entries, which keep the line
 * they came from and the name and date of the document that carried it.
 *
 * Nothing filters by contract for safety. The contract is passed so the server
 * returns the right rows; the reason another contract's rows never appear is
 * that the database refuses them.
 */

const BUCKET = "contract-documents";

export const ENTRY_KINDS = [
  ["accomplishment", "Key accomplishment"],
  ["upcoming", "Upcoming task"],
  ["risk", "Risk or issue"],
  ["decision", "Decision"],
  ["insight", "Consulting insight"],
];

export const DOC_KINDS = [
  "Vendor report", "Meeting minutes", "Correspondence", "Spreadsheet", "Other",
];

export function useComms(contractId, period) {
  const [documents, setDocuments] = useState(null);
  const [entries, setEntries] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!contractId) { setDocuments([]); setEntries([]); return; }
    const [d, e] = await Promise.all([
      supabase.from("comms_documents").select("*")
        .eq("contract_id", contractId).eq("period", period)
        .is("deleted_at", null).order("doc_date", { ascending: false, nullsFirst: false }),
      supabase.from("comms_entries").select("*")
        .eq("contract_id", contractId).eq("period", period)
        .is("deleted_at", null).order("created_at", { ascending: true }),
    ]);
    if (d.error || e.error) setError(friendlyError(d.error || e.error));
    else { setDocuments(d.data ?? []); setEntries(e.data ?? []); setError(""); }
  }, [contractId, period]);

  useEffect(() => { setDocuments(null); setEntries(null); load(); }, [load]);

  /* The file is optional. A document can be recorded with nothing uploaded at
     all, which is the right answer for anything confidential and the cheap
     answer for everything else. */
  const addDocument = useCallback(async (draft, file) => {
    setBusy(true);
    setError("");
    let storage_path = null;
    if (file) {
      /* Scrubbed, because Storage refuses a key with an en dash or an
         apostrophe in it and real filenames are full of both. The name the
         person sees is the one on the row, not this. */
      const path = commsPath(contractId, period, file.name);
      const up = await supabase.storage.from(BUCKET).upload(path, file);
      if (up.error) { setBusy(false); setError(friendlyError(up.error)); return null; }
      storage_path = path;
    }
    const { data, error: failed } = await supabase.from("comms_documents")
      .insert({ ...draft, contract_id: contractId, period, storage_path })
      .select().single();
    setBusy(false);
    if (failed) { setError(friendlyError(failed)); return null; }
    await load();
    return data;
  }, [contractId, period, load]);

  /* Removing a document removes its file and its row. The entries it produced
     stay exactly where they are: the database sets their document_id to null
     and leaves the quote and the source note alone, so what somebody
     confirmed survives the clean-up. */
  const removeDocument = useCallback(async (doc) => {
    setBusy(true);
    if (doc.storage_path) await supabase.storage.from(BUCKET).remove([doc.storage_path]);
    const { error: failed } = await supabase.from("comms_documents")
      .update({ deleted_at: new Date().toISOString() }).eq("id", doc.id);
    setBusy(false);
    if (failed) { setError(friendlyError(failed)); return false; }
    await load();
    return true;
  }, [load]);

  const saveEntry = useCallback(async (draft) => {
    setBusy(true);
    setError("");
    const payload = { ...draft, contract_id: contractId, period };
    delete payload.created_at; delete payload.updated_at;
    delete payload.added_by; delete payload.updated_by;
    const { error: failed } = payload.id
      ? await supabase.from("comms_entries").update(payload).eq("id", payload.id)
      : await supabase.from("comms_entries").insert(payload);
    setBusy(false);
    if (failed) { setError(friendlyError(failed)); return false; }
    await load();
    return true;
  }, [contractId, period, load]);

  const removeEntry = useCallback(async (entry) => {
    const { error: failed } = await supabase.from("comms_entries")
      .update({ deleted_at: new Date().toISOString() }).eq("id", entry.id);
    if (failed) { setError(friendlyError(failed)); return false; }
    await load();
    return true;
  }, [load]);

  /* The bytes back, for reading a file that was uploaded earlier. Storage is
     private, so this goes through the same permission check every other read
     does and returns nothing to anybody who should not have it. */
  const downloadDocument = useCallback(async (doc) => {
    if (!doc.storage_path) return null;
    const { data, error: failed } = await supabase.storage.from(BUCKET).download(doc.storage_path);
    if (failed) { setError(friendlyError(failed)); return null; }
    return data ?? null;
  }, []);

  /* A signed link, made when somebody asks for it and good for a few minutes.
     The bucket is private, so there is no address to leak. */
  const openDocument = useCallback(async (doc) => {
    if (!doc.storage_path) return null;
    const { data, error: failed } = await supabase.storage.from(BUCKET)
      .createSignedUrl(doc.storage_path, 300);
    if (failed) { setError(friendlyError(failed)); return null; }
    return data?.signedUrl ?? null;
  }, []);

  return { documents, entries, error, busy, reload: load,
           addDocument, removeDocument, saveEntry, removeEntry, openDocument,
           downloadDocument };
}
